import {playableVideo,videoFormatHelp} from '@/lib/videoUrl';
import { useState } from "react";
import Modal from "@/components/base/Modal";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import { useTeacherVideoLessons, SUBJECTS } from "@/hooks/useTeacherContent";

const inputClass =
  "w-full h-10 px-3 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 bg-background-50";

interface VideoForm {
  id: string;
  title: string;
  subject: string;
  duration: number;
  thumbnail: string;
  videoUrl: string;
}

const emptyForm: VideoForm = {
  id: "",
  title: "",
  subject: SUBJECTS[0],
  duration: 30,
  thumbnail: "",
  videoUrl: "",
};

export default function VideoDersler() {
  const { data, loading, error, reload, saveVideoLesson, deleteVideoLesson } = useTeacherVideoLessons();
  const list = data ?? [];

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<VideoForm>(emptyForm);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const openCreate = () => {
    setForm(emptyForm);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (v: VideoForm) => {
    setForm(v);
    setFormError(null);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    if (!form.title.trim()) {
      setFormError("Lütfen video başlığını girin.");
      return;
    }
    if (!playableVideo(form.videoUrl.trim())) {
      setFormError(videoFormatHelp);
      return;
    }
    setBusy(true);
    try {
      await saveVideoLesson({
        id: form.id || undefined,
        title: form.title.trim(),
        subject: form.subject,
        duration: form.duration,
        thumbnail: form.thumbnail.trim(),
        videoUrl: form.videoUrl.trim(),
      });
      setShowForm(false);
    } catch {
      setFormError("Video kaydedilirken bir hata oluştu.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmId) return;
    setBusy(true);
    try {
      await deleteVideoLesson(confirmId);
      setConfirmId(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <SectionHeader
          title="Video Dersler"
          description="Video derslerini yükle ve yönet."
        />
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 h-11 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line text-base" aria-hidden="true"></i>
          Video Yükle
        </button>
      </div>

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="ri-play-circle-line"
          title="Henüz video ders yok"
          description="İlk video dersini yüklemek için 'Video Yükle' butonuna tıkla."
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((v) => (
            <div
              key={v.id}
              className="rounded-xl border border-background-200 bg-background-50 p-5 hover:border-primary-300 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold text-base text-foreground-950 truncate">{v.title}</h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{v.subject}</p>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-background-100 text-foreground-600 text-xs font-medium shrink-0">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  {v.duration} dk
                </span>
              </div>

              {v.thumbnail ? (
                <div className="w-full h-36 rounded-lg overflow-hidden bg-background-100 mb-3">
                  <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-full h-36 rounded-lg bg-background-100 mb-3 flex items-center justify-center">
                  <i className="ri-play-circle-line text-4xl text-foreground-300" aria-hidden="true"></i>
                </div>
              )}

              <div className="flex items-center gap-2">
                <a
                  href={v.videoUrl}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex-1 h-10 rounded-md bg-secondary-100 text-secondary-800 text-sm font-semibold hover:bg-secondary-200 transition-colors inline-flex items-center justify-center gap-1.5 whitespace-nowrap"
                >
                  <i className="ri-external-link-line" aria-hidden="true"></i>
                  Videoyu Aç
                </a>
                <button
                  type="button"
                  onClick={() => openEdit(v)}
                  className="w-10 h-10 rounded-md border border-background-200 text-foreground-500 hover:bg-background-100 transition-colors inline-flex items-center justify-center"
                  aria-label="Videoyu düzenle"
                  title="Düzenle"
                >
                  <i className="ri-edit-line" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmId(v.id)}
                  className="w-10 h-10 rounded-md border border-background-200 text-foreground-500 hover:bg-red-50 hover:text-red-600 transition-colors inline-flex items-center justify-center"
                  aria-label="Videoyu sil"
                  title="Sil"
                >
                  <i className="ri-delete-bin-line" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Video formu */}
      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={form.id ? "Videoyu Düzenle" : "Video Ders Yükle"}
        titleId="video-form-title"
        id="video-form-modal"
        maxWidthClass="max-w-lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Başlık</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Örn: TYT Matematik: Fonksiyonlar"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ders</label>
              <select
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                className={inputClass}
              >
                {SUBJECTS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Süre (dk)</label>
              <input
                type="number"
                min={1}
                value={form.duration}
                onChange={(e) => setForm((f) => ({ ...f, duration: Number(e.target.value) }))}
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Video Bağlantısı (URL)</label>
            <input
              type="url"
              value={form.videoUrl}
              onChange={(e) => setForm((f) => ({ ...f, videoUrl: e.target.value }))}
              placeholder="https://.../ders.mp4"
              className={inputClass}
            />
            <p className="text-xs text-foreground-400 mt-1.5">
              YouTube, Vimeo veya doğrudan video dosyası bağlantısı ekleyebilirsin.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Kapak Görseli URL (opsiyonel)</label>
            <input
              type="url"
              value={form.thumbnail}
              onChange={(e) => setForm((f) => ({ ...f, thumbnail: e.target.value }))}
              placeholder="https://..."
              className={inputClass}
            />
          </div>

          {formError && <p className="text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">{formError}</p>}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={busy}
              className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
            >
              {busy ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Silme onayı */}
      <Modal
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        title="Videoyu Sil"
        titleId="delete-video-title"
        id="delete-video-modal"
        maxWidthClass="max-w-sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground-600">
            Bu video dersi silmek istediğine emin misin? Bu işlem geri alınamaz.
          </p>
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setConfirmId(null)}
              className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy}
              className="h-10 px-4 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 transition-colors whitespace-nowrap"
            >
              {busy ? "Siliniyor..." : "Sil"}
            </button>
          </div>
        </div>
      </Modal>

      <PanelLinks
        links={[
          { label: "Derslerim", href: "/ogretmen/derslerim", icon: "ri-book-2-line" },
          { label: "Canlı Dersler", href: "/ogretmen/canli-dersler", icon: "ri-vidicon-line" },
        ]}
      />
    </div>
  );
}